import type { PanelFilters } from '@content-search/domain';
import { ContentType, FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { MultiSelect } from '@soteria/design-system-ve';
import { useCallback, useMemo } from 'react';

import * as styles from './FilterPanel.css';

interface ContentTypeFilterProps {
  panelFilters: PanelFilters;
  updateFilters: (updates: Partial<PanelFilters>) => void;
  isLoading: boolean;
}

const BASE_CONTENT_TYPE_OPTIONS = [
  { value: ContentType.AUDIO, label: 'Audio' },
  { value: ContentType.IMAGE, label: 'Image' },
  { value: ContentType.TEXT, label: 'Text' },
];

const USERNAME_CONTENT_TYPE_OPTION = { value: ContentType.USERNAME, label: 'Username' };

export function ContentTypeFilter({ panelFilters, updateFilters, isLoading }: ContentTypeFilterProps) {
  const isUsernameClassificationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  // Build content type options based on feature flags
  const contentTypeMultiSelectOptions = useMemo(
    () =>
      [...BASE_CONTENT_TYPE_OPTIONS, ...(isUsernameClassificationEnabled ? [USERNAME_CONTENT_TYPE_OPTION] : [])].map(option => ({
        id: option.value,
        label: option.label,
        value: option.value,
      })),
    [isUsernameClassificationEnabled]
  );

  const handleContentTypeChange = useCallback(
    (selectedTypes: string[]) => {
      const updates: Partial<PanelFilters> = { contentTypes: selectedTypes };

      // If audio is not included in the selected content types, clear the language filter
      if (selectedTypes.length > 0 && !selectedTypes.includes(ContentType.AUDIO)) {
        updates.languageDetected = undefined;
      }

      updateFilters(updates);
    },
    [updateFilters]
  );

  return (
    <div className={styles.filterSection}>
      <div className={styles.filterLabel}>Content Type</div>
      <MultiSelect
        aria-label="Filter by content type"
        disabled={isLoading}
        options={contentTypeMultiSelectOptions}
        placeholder="All"
        selectedValues={panelFilters.contentTypes || []}
        onSelectionChange={handleContentTypeChange}
      />
    </div>
  );
}
